/**
 * Dziki Pan PDP (template mydlo-sb-01) runtime.
 * Gallery with thumb-jump + swipe, pack/qty pricing from real variants,
 * AJAX add-to-cart through the theme's standard cart events, accordions,
 * mobile sticky CTA, header cart count.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/* ---------- money ---------- */
function formatMoney(minor, format, keepDecimals = false) {
  const useComma = /amount_with_comma_separator|amount_no_decimals_with_comma_separator/.test(format || '');
  const noDecimals = /amount_no_decimals/.test(format || '');
  const value = Math.round(minor) / 100;
  let [whole, frac] = value.toFixed(2).split('.');
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, useComma ? '.' : ',');
  const stripZeros = !keepDecimals && frac === '00';
  let amount = noDecimals || stripZeros ? whole : `${whole}${useComma ? ',' : '.'}${frac}`;
  return (format || '{{amount}}').replace(/{{\s*\w+\s*}}/, amount);
}

/* ---------- cart ---------- */
/** open Horizon's cart drawer when present (header link still falls back to /cart) */
function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
}

async function addToCart(host, lines) {
  const routes = (typeof Theme !== 'undefined' ? Theme.routes : null) ?? {};
  const addUrl = routes.cart_add_url ?? '/cart/add.js';
  const cartUrl = `${routes.cart_url ?? '/cart'}.js`;
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    });
    const json = await response.json();
    const didError = Boolean(json.status);
    if (didError) {
      host.dispatchEvent(
        new CartErrorEvent({
          error: json.message || json.description || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: json.description, errors: json.errors },
        })
      );
    }
    const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError, sections: json.sections, items: cart.items, source: 'dp-pdp', sourceId: host.id || '', itemCount },
    });
    return didError ? { ok: false, message: json.description || json.message } : { ok: true, cart };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: error instanceof Error ? error.message : String(error) };
  }
}

/* ---------- gallery (infinite loop via edge clones) ---------- */
class DpGallery extends HTMLElement {
  #abort = new AbortController();
  #raf = 0;
  #settle = 0;
  #resize = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.track = this.querySelector('[data-dp-track]');
    this.thumbs = [...this.querySelectorAll('[data-dp-thumb]')];
    this.thumbsWrap = this.querySelector('[data-dp-thumbs]');
    this.ring = this.querySelector('[data-dp-thumbring]');
    if (this.ring && this.thumbsWrap && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.placeRing());
      this.ro.observe(this.thumbsWrap);
      requestAnimationFrame(() => { this.placeRing(); requestAnimationFrame(() => this.thumbsWrap.setAttribute('data-ready', '')); });
    }
    this.dots = [...this.querySelectorAll('[data-dp-dot]')];
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.track) return;
    const real = [...this.track.querySelectorAll('[data-dp-slide]')];
    this.n = real.length;
    this.index = 0;
    this.loop = this.n > 1;

    if (this.loop) {
      const first = real[0].cloneNode(true);
      const last = real[this.n - 1].cloneNode(true);
      for (const c of [first, last]) { c.setAttribute('data-dp-clone', ''); c.setAttribute('aria-hidden', 'true'); c.querySelectorAll('img').forEach((i) => i.setAttribute('loading', 'lazy')); }
      this.track.prepend(last);
      this.track.append(first);
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = this.track.clientWidth;
      requestAnimationFrame(() => { this.track.style.scrollBehavior = ''; });
    }

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i), { signal }));
    this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i), { signal }));
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.settle(), { signal });
    this.track.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      },
      { signal }
    );
    this.#resize = new ResizeObserver(() => this.jump(this.slot(this.index)));
    this.#resize.observe(this.track);
    this.setActive(0);
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    this.#abort.abort();
    this.#resize?.disconnect();
    if (this.#raf) cancelAnimationFrame(this.#raf);
    if (this.#settle) clearTimeout(this.#settle);
  }

  /** physical slot in the track for a real index */
  slot(i) { return this.loop ? i + 1 : i; }

  jump(slotIndex) {
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = slotIndex * this.track.clientWidth;
    requestAnimationFrame(() => { this.track.style.scrollBehavior = ''; });
  }

  go(i) {
    if (!this.n) return;
    let target;
    if (this.loop) {
      // allow stepping onto the clones for a seamless wrap
      target = i < 0 ? 0 : i >= this.n ? this.n + 1 : i + 1;
    } else {
      target = Math.max(0, Math.min(this.n - 1, i));
    }
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: this.reduce ? 'auto' : 'smooth' });
    this.setActive(((i % this.n) + this.n) % this.n);
  }

  onScroll() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const w = this.track.clientWidth || 1;
      const slotIdx = Math.round(this.track.scrollLeft / w);
      const real = this.loop ? ((slotIdx - 1 + this.n) % this.n) : slotIdx;
      this.setActive(real);
    });
    if (!('onscrollend' in window)) {
      if (this.#settle) clearTimeout(this.#settle);
      this.#settle = setTimeout(() => this.settle(), 120);
    }
  }

  /** after scrolling stops: if we're on a clone, jump to the real slide without animation */
  settle() {
    if (!this.loop) return;
    const w = this.track.clientWidth || 1;
    const slotIdx = Math.round(this.track.scrollLeft / w);
    if (slotIdx === 0) this.jump(this.n);
    else if (slotIdx === this.n + 1) this.jump(1);
  }

  setActive(i) {
    this.index = i;
    this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
    this.dots.forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
    this.thumbs[i]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    this.placeRing();
  }

  /** slide the ring to the active thumb (position relative to the scrollable thumbs wrap) */
  placeRing() {
    const t = this.thumbs[this.index];
    if (!t || !this.ring) return;
    this.ring.style.setProperty('--dp-ring-x', `${t.offsetLeft}px`);
    this.ring.style.setProperty('--dp-ring-y', `${t.offsetTop}px`);
    this.ring.style.width = `${t.offsetWidth}px`;
    this.ring.style.height = `${t.offsetHeight}px`;
  }
}

/* ---------- announcement carousel ---------- */
class DpAnnouncement extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;

  connectedCallback() {
    const { signal } = this.#abort;
    this.slides = [...this.querySelectorAll('[data-dp-ann-slide]')];
    this.index = Math.max(0, this.slides.findIndex((s) => s.hasAttribute('data-active')));
    if (this.slides.length < 2) return;
    this.querySelector('[data-dp-ann-prev]')?.addEventListener('click', () => this.step(-1, true), { signal });
    this.querySelector('[data-dp-ann-next]')?.addEventListener('click', () => this.step(1, true), { signal });
    let x0 = null;
    this.addEventListener('touchstart', (e) => (x0 = e.touches[0]?.clientX ?? null), { passive: true, signal });
    this.addEventListener('touchend', (e) => { if (x0 === null) return; const dx = (e.changedTouches[0]?.clientX ?? x0) - x0; if (Math.abs(dx) > 30) this.step(dx < 0 ? 1 : -1, true); x0 = null; }, { passive: true, signal });
    this.addEventListener('mouseenter', () => this.stop(), { signal });
    this.addEventListener('mouseleave', () => this.start(), { signal });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()), { signal });
    this.start();
  }

  disconnectedCallback() { this.#abort.abort(); this.stop(); }

  start() {
    this.stop();
    const sec = parseInt(this.dataset.interval || '0', 10);
    if (sec > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#timer = window.setInterval(() => this.step(1, false), sec * 1000);
  }

  stop() { if (this.#timer) { clearInterval(this.#timer); this.#timer = 0; } }

  step(dir, user) {
    const n = this.slides.length;
    const next = (this.index + dir + n) % n;
    this.style.setProperty('--dp-ann-dir', dir > 0 ? '18px' : '-18px');
    const cur = this.slides[this.index];
    cur.removeAttribute('data-active'); cur.setAttribute('data-leaving', ''); cur.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => cur.removeAttribute('data-leaving'), 400);
    const nx = this.slides[next];
    nx.setAttribute('data-active', ''); nx.setAttribute('aria-hidden', 'false');
    this.index = next;
    if (user) this.start();
  }
}

/* ---------- header drawer ---------- */
class DpHeader extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.burger = this.querySelector('[data-dp-burger]');
    this.drawer = this.querySelector('[data-dp-drawer]');
    this.backdrop = this.querySelector('[data-dp-drawer-backdrop]');
    if (!this.burger || !this.drawer) return;
    this.burger.addEventListener('click', () => this.toggle(), { signal });
    this.querySelector('[data-dp-drawer-close]')?.addEventListener('click', () => this.close(), { signal });
    this.backdrop?.addEventListener('click', () => this.close(), { signal });
    this.drawer.addEventListener('click', (e) => { if (e.target instanceof HTMLAnchorElement) this.close(); }, { signal });
    for (const a of this.querySelectorAll('.dp-header__cart, .dp-menu__cart')) {
      a.addEventListener('click', (e) => { if (openCartDrawer()) { e.preventDefault(); this.close(); } }, { signal });
    }
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); }, { signal });
  }

  disconnectedCallback() { this.#abort.abort(); }

  toggle() { this.drawer.hasAttribute('data-open') ? this.close() : this.open(); }

  open() {
    if (this.backdrop) { this.backdrop.hidden = false; requestAnimationFrame(() => this.backdrop.setAttribute('data-visible', '')); }
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.setAttribute('data-open', '');
    this.burger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.drawer.querySelector('a, button')?.focus({ preventScroll: true });
  }

  close() {
    if (!this.drawer.hasAttribute('data-open')) return;
    this.drawer.removeAttribute('data-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    this.burger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (this.backdrop) { this.backdrop.removeAttribute('data-visible'); setTimeout(() => { this.backdrop.hidden = true; }, 300); }
    this.burger.focus({ preventScroll: true });
  }
}

/* ---------- buy box ---------- */
class DpBuyBox extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.format = this.dataset.moneyFormat || '{{amount}}';
    this.days = parseInt(this.dataset.days || '35', 10) || 35;
    this.qty = 1;
    this.min = 1;
    this.max = parseInt(this.dataset.maxQty || '10', 10) || 10;

    try {
      this.variants = JSON.parse(this.querySelector('[data-dp-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.packs = [...this.querySelectorAll('input[data-dp-pack]')];
    const wanted = new URLSearchParams(location.search).get('pack');
    if (wanted) {
      const hit = this.packs.find((p) => p.dataset.units === wanted || p.dataset.packValue === wanted);
      if (hit && !hit.checked) {
        hit.checked = true;
        for (const p of this.packs) p.closest('[data-dp-pack-card]')?.toggleAttribute('data-selected', p === hit);
      }
    }
    this.slots = [...this.querySelectorAll('[data-dp-slot-wrap]')];
    this.scent = this.querySelector('[data-dp-scent]');
    this.qtyEl = this.querySelector('[data-dp-qty]');
    this.priceEls = [...document.querySelectorAll('[data-dp-price]')];
    this.perBarEl = this.querySelector('[data-dp-per-bar]');
    this.ctaButtons = [...document.querySelectorAll('[data-dp-cta]')];
    this.sticky = document.querySelector('[data-dp-sticky]');

    this.addEventListener('change', (e) => this.onChange(e), { signal });
    this.addEventListener('click', (e) => {
      const chip = e.target instanceof Element ? e.target.closest('[data-dp-slot-chip]') : null;
      if (!chip) return;
      const wrap = chip.closest('[data-dp-slot-wrap]');
      const select = wrap?.querySelector('select[data-dp-slot]');
      if (!select) return;
      select.value = chip.dataset.value || '';
      for (const c of wrap.querySelectorAll('[data-dp-slot-chip]')) c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal });
    this.querySelector('[data-dp-qty-down]')?.addEventListener('click', () => this.setQty(this.qty - 1), { signal });
    this.querySelector('[data-dp-qty-up]')?.addEventListener('click', () => this.setQty(this.qty + 1), { signal });
    for (const btn of this.ctaButtons) btn.addEventListener('click', () => this.add(btn), { signal });

    if (this.sticky) {
      const anchor = this.querySelector('[data-dp-cta]') ?? this;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky?.toggleAttribute('data-visible', show);
          this.sticky?.toggleAttribute('inert', !show);
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);
    }

    this.render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.observer?.disconnect();
  }

  onChange(e) {
    const t = e.target;
    if (t instanceof HTMLSelectElement && t.hasAttribute('data-dp-scent')) {
      const url = t.selectedOptions[0]?.dataset.url;
      if (t.dataset.mode === 'products' && url) {
        window.location.assign(url);
        return;
      }
    }
    this.render();
  }

  setQty(n) {
    this.qty = Math.min(this.max, Math.max(this.min, n));
    this.render();
  }

  get selectedPack() {
    return this.packs.find((p) => p.checked) ?? this.packs[0] ?? null;
  }

  /** Resolve variant + totals for the current pack/scent. */
  resolve() {
    const pack = this.selectedPack;
    if (!pack) return null;
    const mode = this.dataset.packMode || 'quantity';
    const units = parseInt(pack.dataset.units || '1', 10) || 1;
    let variantId = pack.dataset.variant ? Number(pack.dataset.variant) : null;
    let unitPrice = pack.dataset.unitPrice ? Number(pack.dataset.unitPrice) : null;
    let total = pack.dataset.total ? Number(pack.dataset.total) : null;
    let available = pack.dataset.available !== 'false';
    let addQty = parseInt(pack.dataset.addQty || '1', 10) || 1;
    let lines = null;

    if (mode === 'mix') {
      const active = this.slots.slice(0, units).map((w) => w.querySelector('select'));
      const prices = active.map((sel) => Number(sel?.selectedOptions[0]?.dataset.price || 0));
      const sum = prices.reduce((a, b) => a + b, 0);
      total = pack.dataset.packTotal ? Number(pack.dataset.packTotal) : sum;
      unitPrice = Math.round(total / units);
      available = active.every((sel) => sel && sel.selectedOptions[0]?.dataset.available !== 'false');
      variantId = Number(active[0]?.value || 0) || null;
      const counts = new Map();
      for (const sel of active) { const id = Number(sel?.value || 0); if (id) counts.set(id, (counts.get(id) || 0) + 1); }
      lines = [...counts].map(([id, quantity]) => ({ id, quantity }));
      addQty = 1;
      return { variantId, units, unitPrice, total, available, addQty, lines };
    }

    // Option-based scent: re-resolve against the embedded variants list.
    if (this.scent && this.scent.dataset.mode === 'option' && this.variants.length) {
      const scentValue = this.scent.value;
      const packValue = pack.dataset.packValue || '';
      const match = this.variants.find((v) => {
        const opts = v.options || [];
        if (!opts.includes(scentValue)) return false;
        return mode === 'variant' ? opts.includes(packValue) : true;
      });
      if (match) {
        variantId = match.id;
        available = match.available;
        if (mode === 'variant') {
          total = match.price;
          unitPrice = Math.round(match.price / units);
          addQty = 1;
        } else {
          unitPrice = match.price;
          total = match.price * units;
          addQty = units;
        }
      }
    }

    if (total === null && unitPrice !== null) total = unitPrice * units;
    if (unitPrice === null && total !== null) unitPrice = Math.round(total / units);
    return { variantId, units, unitPrice: unitPrice ?? 0, total: total ?? 0, available, addQty, lines };
  }

  /** unlock progress bar: fill up to the selected pack, flip locks to checks */
  renderTiers() {
    const tiers = this.querySelector('[data-dp-tiers]');
    if (!tiers) return;
    const steps = [...tiers.querySelectorAll('[data-dp-tier-step]')];
    const sel = this.packs.findIndex((p) => p.checked);
    const n = Math.max(steps.length - 1, 1);
    tiers.style.setProperty('--dp-tiers-progress', `${(Math.max(sel, 0) / n) * 100}%`);
    steps.forEach((st, i) => st.toggleAttribute('data-unlocked', i <= sel));
  }

  render() {
    const r = this.resolve();
    if (this.qtyEl) this.qtyEl.textContent = String(this.qty);
    for (const p of this.packs) p.closest('[data-dp-pack-card]')?.toggleAttribute('data-selected', p.checked);
    if (!r) return;
    if (this.slots.length) {
      this.slots.forEach((w, i) => { w.hidden = i >= r.units; });
      const slotsBox = this.querySelector('[data-dp-slots]');
      if (slotsBox) {
        this.slotsHome ??= slotsBox.parentElement;
        const show = this.dataset.packMode === 'mix' && r.units >= 2;
        slotsBox.hidden = !show;
        const host = show ? this.packs.find((p) => p.checked)?.closest('[data-dp-pack-card]')?.querySelector('[data-dp-slot-host]') : null;
        if (host && slotsBox.parentElement !== host) host.appendChild(slotsBox);
        if (!host && this.slotsHome && slotsBox.parentElement !== this.slotsHome) this.slotsHome.appendChild(slotsBox);
      }
    }
    this.renderTiers();
    const grand = r.total * this.qty;
    const perBar = formatMoney(r.unitPrice, this.format, true);
    const perDay = formatMoney(r.unitPrice / this.days, this.format, true);
    const grandText = formatMoney(grand, this.format);
    for (const el of this.priceEls) {
      if (el.textContent !== grandText) {
        el.textContent = grandText;
        el.classList.remove('dp-pulse');
        void el.offsetWidth;
        el.classList.add('dp-pulse');
      }
    }
    if (this.perBarEl) {
      this.perBarEl.textContent = `${perBar} ${this.perBarEl.dataset.perLabel || '/ kostka'} · ${perDay} ${this.perBarEl.dataset.dayLabel || 'dziennie'}`;
    }
    for (const btn of this.ctaButtons) {
      const label = btn.querySelector('[data-dp-cta-label]');
      const tpl = btn.dataset.template || 'Do koszyka · [price]';
      if (label) label.textContent = tpl.replace('[price]', grandText);
      btn.disabled = !r.available || !r.variantId;
    }
  }

  async add(button) {
    const r = this.resolve();
    if (!r || !r.variantId || !r.available) return;
    const label = button.querySelector('[data-dp-cta-label]');
    const original = label?.textContent;
    button.disabled = true;
    if (label && button.dataset.adding) label.textContent = button.dataset.adding;
    const lines = r.lines
      ? r.lines.map((l) => ({ id: l.id, quantity: l.quantity * this.qty }))
      : [{ id: r.variantId, quantity: r.addQty * this.qty }];
    const result = await addToCart(this, lines);
    if (result.ok) openCartDrawer();
    if (label) label.textContent = result.ok ? button.dataset.added || original : button.dataset.error || result.message || original;
    window.setTimeout(() => {
      if (label && original) label.textContent = original;
      this.render();
    }, 1800);
  }
}

/* ---------- accordion ---------- */
class DpAccordion extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    for (const btn of this.querySelectorAll('[data-dp-acc]')) {
      btn.addEventListener('click', () => this.toggle(btn), { signal: this.#abort.signal });
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  toggle(btn) {
    const body = document.getElementById(btn.getAttribute('aria-controls') || '');
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (body) {
      body.hidden = false;
      body.toggleAttribute('data-open', !open);
    }
    const icon = btn.querySelector('[data-dp-acc-icon]');
    if (icon) icon.textContent = open ? '+' : '−';
  }
}

/* ---------- header cart count ---------- */
function watchCartCount() {
  const targets = document.querySelectorAll('[data-dp-cart-count]');
  if (!targets.length) return;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        for (const el of targets) el.textContent = String(count);
        for (const b of document.querySelectorAll('[data-dp-cart-bubble]')) b.classList.toggle('is-empty', count === 0);
      })
      .catch(() => {});
  });
}

function revealOnScroll() {
  const targets = document.querySelectorAll('.dp-reveal');
  if (!targets.length) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    for (const el of targets) el.setAttribute('data-shown', '');
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.setAttribute('data-shown', ''); io.unobserve(e.target); }
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
  );
  for (const el of targets) io.observe(el);
}

const definitions = [
  ['dp-gallery', DpGallery],
  ['dp-header', DpHeader],
  ['dp-announcement', DpAnnouncement],
  ['dp-buy-box', DpBuyBox],
  ['dp-accordion', DpAccordion],
];
for (const [name, ctor] of definitions) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}
watchCartCount();
revealOnScroll();
